import { useRef, useState } from 'react';
import { useIntl } from 'react-intl';
import { useNavigate } from 'react-router-dom';
import { axiosErrorMessage } from '@api/errors';
import useSnackbar from '@hooks/useSnackbar';
import { LLM_MODEL_AUTO } from '@utils/llmModel';
import { setPendingUploadFiles } from '@utils/pendingFiles';
import type { BuilderRouteState } from '../builderPrefill';
import { useAppBuilderActions } from '../useAppBuilder';
import { builderPath } from '../userAppStatus';
import { nameFromPrompt, taglineFromPrompt } from './nameFromPrompt';

export interface AppBrief {
  readonly text: string;
  readonly files: readonly File[];
  readonly exampleName?: string;
  readonly allowedSkills?: readonly string[];
  /** Empty or Auto leaves the app on the default model. */
  readonly llmModel?: string;
}

export const useCreateAppFromPrompt = () => {
  const intl = useIntl();
  const navigate = useNavigate();
  const { showError } = useSnackbar();
  const actions = useAppBuilderActions();
  const inFlight = useRef(false);
  const [launched, setLaunched] = useState(false);

  /** Resolves to whether the builder was opened with the new app. */
  const create = async ({ text, files, exampleName, allowedSkills, llmModel }: AppBrief): Promise<boolean> => {
    if (inFlight.current) return false;
    inFlight.current = true;
    const prompt = text.trim();
    let appKey: string;
    try {
      ({ appKey } = await actions.create({
        name: exampleName ?? nameFromPrompt(prompt) ?? undefined,
        tagline: taglineFromPrompt(prompt) ?? undefined,
        allowedSkills: allowedSkills?.length ? allowedSkills : undefined,
        llmModel: llmModel && llmModel !== LLM_MODEL_AUTO ? llmModel : undefined,
      }));
    } catch (err) {
      inFlight.current = false;
      showError(
        axiosErrorMessage(
          err,
          intl.formatMessage({ id: 'apps.builder.error.create', defaultMessage: 'Your app could not be created.' }),
        ),
      );
      return false;
    }
    setPendingUploadFiles(files.length > 0 ? [...files] : undefined);
    setLaunched(true);
    const state: BuilderRouteState = { prefill: prompt, send: true };
    navigate(builderPath(appKey), { state });
    return true;
  };

  return { create, creating: actions.isCreating || launched };
};
